import { useEffect, useRef, useCallback } from 'react'
import { getKyousouApiClient } from '../services/network'
import { useAuth } from './auth/use_auth'

/**
 * 段階的な滞在時間測定のための設定
 * 1秒 → 10秒 → 30秒 → 60秒 → 以降60秒ごと
 */
const INTERVALS = [1, 10, 30, 60] as const
const REPEAT_INTERVAL = 60

interface UseVisitorTrackingOptions {
  enterpriseId: string | undefined
}

/**
 * 企業ページ訪問の追跡を行うカスタムフック
 *
 * @param enterpriseId - 訪問したクチコミ企業ID
 */
export const useVisitorTracking = ({
  enterpriseId,
}: UseVisitorTrackingOptions) => {
  const apiClient = getKyousouApiClient()
  const { user } = useAuth()
  const currentTimer = useRef<NodeJS.Timeout | null>(null)
  const intervalIndex = useRef(0)
  const visitorStatId = useRef<string | null>(null)

  /**
   * 滞在時間を送信する
   */
  const sendDuration = useCallback(
    async (duration: number) => {
      // 自分の企業への訪問は記録しない
      if (!enterpriseId || user?.kuchikomiEnterpriseId == enterpriseId) return

      try {
        const response = await apiClient.stat.recordCreate({
          visitedEnterpriseId: enterpriseId, // 訪問された企業ID
          stayDuration: duration, // 滞在時間
          visitorStatId: visitorStatId.current, // 前回のレスポンスで受け取った訪問統計レコードID（初回はnull）
        })

        if (response.data?.visitorStatId) {
          visitorStatId.current = response.data.visitorStatId
        }
      } catch (error) {
        // エラーはコンソールに出力するだけで、ユーザーには表示しない
        console.log('訪問記録エラー:', error)
      }
    },
    [enterpriseId, user?.kuchikomiEnterpriseId, apiClient.stat.recordCreate]
  )

  /**
   * 次のタイマーをセット
   */
  const setNextTimer = useCallback(() => {
    // 既存タイマーをクリア
    if (currentTimer.current) clearTimeout(currentTimer.current)

    let waitTime: number
    let sendValue: number

    if (intervalIndex.current < INTERVALS.length) {
      // 初期段階: 1秒→10秒→30秒→60秒
      waitTime = INTERVALS[intervalIndex.current]
      sendValue = waitTime
      intervalIndex.current++
    } else {
      // 繰り返し段階: 60秒ごと
      waitTime = REPEAT_INTERVAL
      sendValue = REPEAT_INTERVAL
    }

    // タイマーを設定して再帰実行
    currentTimer.current = setTimeout(() => {
      sendDuration(sendValue)
      setNextTimer()
    }, waitTime * 1000)
  }, [sendDuration])

  useEffect(() => {
    // 自分の企業への訪問は記録しない
    if (!enterpriseId || user?.kuchikomiEnterpriseId == enterpriseId) {
      return
    }

    // タイマー開始処理
    intervalIndex.current = 0
    visitorStatId.current = null
    setNextTimer()

    // ページ離脱時 タイマーをクリア
    return () => {
      if (currentTimer.current) {
        clearTimeout(currentTimer.current)
        currentTimer.current = null
      }
      intervalIndex.current = 0
      visitorStatId.current = null
    }
  }, [enterpriseId, user?.kuchikomiEnterpriseId])
}
